import { atom, useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useContext, useMemo, useRef } from 'react';
import { useAnimatedReaction, useSharedValue } from 'react-native-reanimated';
import { useScreenSize } from '@banhala/ui-kit';
import { scheduleOnRN } from 'react-native-worklets';
import { ScrollContext } from '../context/ScrollContext';
import { isNotNil } from 'shared/src/utils';
import { type View } from 'react-native';

const sectionVisibilityAtom = atom<Record<string, boolean>>({});

export const useIsSectionVisible = (key: string) => {
  const derivedAtom = useMemo(
    () => atom(get => isNotNil(get(sectionVisibilityAtom)[key])),
    [key],
  );
  return useAtomValue(derivedAtom);
};

export const useMarkSectionVisible = () => {
  const set = useSetAtom(sectionVisibilityAtom);
  return useCallback(
    (key: string) => {
      set(prev => {
        if (prev[key]) return prev;
        return { ...prev, [key]: true };
      });
    },
    [set],
  );
};

/**
 * 컴포넌트가 뷰포트에 진입했는지 감지하는 훅.
 * 중첩된 서브 섹션 컴포넌트에서 사용.
 *
 * 반환된 ref를 View에 연결하면,
 * 스크롤 시 해당 요소가 화면에 보이는 순간 visibility atom을 업데이트.
 */
export const useElementViewportTracking = (
  visibilityKey: string,
  offset = 0,
) => {
  const { scrollY } = useContext(ScrollContext);
  const { height: screenHeight } = useScreenSize();
  const isMarked = useSharedValue(false);
  const viewRef = useRef<View>(null);
  const markVisible = useMarkSectionVisible();

  const checkVisibility = useCallback(() => {
    if (isMarked.value) return;
    viewRef.current?.measureInWindow((_x, y, _w, h) => {
      if (h > 0 && y < screenHeight - offset) {
        isMarked.value = true;

        markVisible(visibilityKey);
      }
    });
  }, [isMarked, screenHeight, offset, markVisible, visibilityKey]);

  useAnimatedReaction(
    () => scrollY?.value ?? 0,
    () => {
      if (isMarked.value) return;
      scheduleOnRN(checkVisibility);
    },
    [checkVisibility],
  );

  return viewRef;
};
